import React, { useState, useMemo } from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Nav, Card } from 'react-bootstrap';
import { FiSettings, FiFileText, FiLock, FiBriefcase, FiCheck, FiBarChart2, FiUsers } from 'react-icons/fi';
import { isAdmin } from '../utils/auth';
import './styles/AdminLayout.css';

// Import admin sub-components
import AdminDashboard from './sections/AdminDashboard';
import UsersPage from './UsersPage';
import SystemSettings from './sections/SystemSettings';
import AuditLogs from './sections/AuditLogs';
import RoleManagement from './sections/RoleManagement';
import DepartmentManagement from './sections/DepartmentManagement';
import PermissionManagement from './sections/PermissionManagement';
import LookupManagement from './sections/LookupManagement';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  const adminSections = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: FiBarChart2,
      component: AdminDashboard,
      description: 'System overview and statistics'
    },
    {
      id: 'users',
      label: 'Users',
      icon: FiUsers,
      component: UsersPage,
      description: 'Manage users'
    },
    {
      id: 'audit',
      label: 'Audit Logs',
      icon: FiFileText,
      component: AuditLogs,
      description: 'View system activity logs'
    },
    {
      id: 'roles',
      label: 'Roles',
      icon: FiLock,
      component: RoleManagement,
      description: 'Manage user roles'
    },
    {
      id: 'departments',
      label: 'Departments',
      icon: FiBriefcase,
      component: DepartmentManagement,
      description: 'Manage departments'
    },
    {
      id: 'permissions',
      label: 'Permissions',
      icon: FiCheck,
      component: PermissionManagement,
      description: 'Manage permissions'
    },
    {
      id: 'lookups',
      label: 'Master Tables',
      icon: FiBriefcase,
      component: LookupManagement,
      description: 'Manage dropdown master data'
    },
    {
      id: 'settings',
      label: 'System Settings',
      icon: FiSettings,
      component: SystemSettings,
      description: 'Configure system settings'
    }
  ], []);

  const currentSection = adminSections.find((section) => section.id === activeTab) || adminSections[0];
  const CurrentComponent = currentSection?.component;

  // Check admin access and redirect if needed
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const legacyTab = searchParams.get('tab');
    const pathSegment = location.pathname.split('/')[2];
    const requestedTab = pathSegment || legacyTab || 'dashboard';
    const validTab = adminSections.some((section) => section.id === requestedTab)
      ? requestedTab
      : 'dashboard';

    setActiveTab(validTab);

    if (location.pathname === '/admin') {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [location.pathname, location.search, navigate, adminSections]);

  // Don't render admin content for non-admin users
  if (!isAdmin()) {
    return null;
  }

  return (
    <Container fluid className="admin-layout py-4">
      <div className="admin-header-wrap mb-4">
        <Card className="admin-header-card border-0 shadow-sm">
          <Card.Body>
            <div className="admin-header d-flex align-items-center gap-3">
              <div className="icon-chip icon-chip-lg">
                <FiSettings />
              </div>
              <div>
                <h1 className="mb-0">Admin Panel</h1>
                <p className="text-muted mb-0">{currentSection?.description}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="admin-nav mb-3">
        <Card className="admin-nav-card shadow-sm border-0">
          <Card.Body className="admin-nav-body">
            <Nav variant="tabs" className="admin-tabs">
              {adminSections.map((section) => {
                const Icon = section.icon;
                return (
                  <Nav.Item key={section.id}>
                    <Nav.Link
                      eventKey={section.id}
                      active={activeTab === section.id}
                      onClick={() => {
                        setActiveTab(section.id);
                        navigate(`/admin/${section.id}`);
                      }}
                      className="admin-tab-link"
                    >
                      <Icon size={16} className="me-1" />
                      <span>{section.label}</span>
                    </Nav.Link>
                  </Nav.Item>
                );
              })}
            </Nav>
          </Card.Body>
        </Card>
      </div>

      <div className="admin-content">
        {CurrentComponent && <CurrentComponent />}
      </div>
    </Container>
  );
};

export default AdminLayout;
